/**
 * SelectrManager.ts - Basic Selection State Management
 * 
 * CHANGE HISTORY:
 * - 251115_2200 [pending] danielbeer: Create selection state manager
 * 
 * Manages selection state for any list or collection of items.
 * Provides basic selection operations, history, and persistence.
 */

export interface SelectionState {
  selected: Set<string>;
  anchorId: string | null;
  timestamp: number;
}

export interface SelectionOptions {
  mode?: 'single' | 'multiple';
  maxHistory?: number;
  persistent?: boolean;
  storageKey?: string;
}

export type SelectionMode = 'replace' | 'add' | 'toggle' | 'range';

export type SelectionEventType = 'select' | 'deselect' | 'clear' | 'change';

export interface SelectionEvent {
  type: SelectionEventType;
  ids: string[];
  timestamp: number;
}

export type SelectionListener = (event: SelectionEvent) => void;

/**
 * SelectrManager - Basic selection state management
 * 
 * Handles selection state for any collection of items.
 * Supports single/multi-select, history, and persistence.
 */
export class SelectrManager {
  protected selected: Set<string> = new Set();
  protected anchorId: string | null = null;
  protected history: SelectionState[] = [];
  protected listeners: Map<SelectionEventType, Set<SelectionListener>> = new Map();
  protected options: Required<SelectionOptions>;

  constructor(options: SelectionOptions = {}) {
    this.options = {
      mode: options.mode || 'multiple',
      maxHistory: options.maxHistory || 50,
      persistent: options.persistent || false,
      storageKey: options.storageKey || 'selectr_state'
    };

    // Initialize event listeners
    this.listeners.set('select', new Set());
    this.listeners.set('deselect', new Set());
    this.listeners.set('clear', new Set());
    this.listeners.set('change', new Set());

    // Load persisted state if enabled
    if (this.options.persistent) {
      this.loadState();
    }
  }

  // ========================================
  // BASIC SELECTION OPERATIONS
  // ========================================

  /**
   * Select one or more items
   */
  select(id: string | string[], mode: SelectionMode = 'replace'): void {
    const ids = Array.isArray(id) ? id : [id];
    const previousState = this.captureState();

    switch (mode) {
      case 'replace':
        this.selected.clear();
        ids.forEach(itemId => this.selected.add(itemId));
        this.anchorId = ids[0] || null;
        break;

      case 'add':
        ids.forEach(itemId => this.selected.add(itemId));
        this.anchorId = ids[ids.length - 1] || this.anchorId;
        break;

      case 'toggle':
        ids.forEach(itemId => {
          if (this.selected.has(itemId)) {
            this.selected.delete(itemId);
          } else {
            this.selected.add(itemId);
          }
        });
        this.anchorId = ids[ids.length - 1] || this.anchorId;
        break;

      case 'range':
        // Range selection handled by subclass
        ids.forEach(itemId => this.selected.add(itemId));
        break;
    }

    // Enforce single-select mode
    if (this.options.mode === 'single' && this.selected.size > 1) {
      const lastId = ids[ids.length - 1];
      this.selected.clear();
      this.selected.add(lastId);
      this.anchorId = lastId;
    }

    this.pushHistory(previousState);
    this.emit('select', ids);
    this.emit('change', Array.from(this.selected));
    this.persist();
  }

  /**
   * Deselect one or more items
   */
  deselect(id: string | string[]): void {
    const ids = Array.isArray(id) ? id : [id];
    const previousState = this.captureState();

    ids.forEach(itemId => this.selected.delete(itemId));

    // Clear anchor if it was deselected
    if (this.anchorId && ids.includes(this.anchorId)) {
      this.anchorId = this.selected.size > 0 
        ? Array.from(this.selected)[0] 
        : null;
    }

    this.pushHistory(previousState);
    this.emit('deselect', ids);
    this.emit('change', Array.from(this.selected));
    this.persist();
  }

  /**
   * Clear all selections
   */
  clearSelection(): void {
    if (this.selected.size === 0) return;

    const previousState = this.captureState();
    const previousIds = Array.from(this.selected);

    this.selected.clear();
    this.anchorId = null;

    this.pushHistory(previousState);
    this.emit('clear', previousIds);
    this.emit('change', []);
    this.persist();
  }

  /**
   * Select all items (requires item IDs)
   */
  selectAll(itemIds: string[]): void {
    this.select(itemIds, 'replace');
  }

  // ========================================
  // QUERY METHODS
  // ========================================

  /**
   * Get all selected item IDs
   */
  getSelected(): string[] {
    return Array.from(this.selected);
  }

  /**
   * Check if an item is selected
   */
  isSelected(id: string): boolean {
    return this.selected.has(id);
  }

  /**
   * Get selection count
   */
  getSelectionCount(): number {
    return this.selected.size;
  }

  /**
   * Check if any items are selected
   */
  hasSelection(): boolean {
    return this.selected.size > 0;
  }

  /**
   * Get anchor ID (for range selection)
   */
  getAnchor(): string | null {
    return this.anchorId;
  }

  // ========================================
  // HISTORY MANAGEMENT
  // ========================================

  /**
   * Capture current state
   */
  protected captureState(): SelectionState {
    return {
      selected: new Set(this.selected),
      anchorId: this.anchorId,
      timestamp: Date.now()
    };
  }

  /**
   * Push state to history
   */
  protected pushHistory(state: SelectionState): void {
    this.history.push(state);

    // Limit history size
    if (this.history.length > this.options.maxHistory) {
      this.history.shift();
    }
  }

  /**
   * Undo last selection change
   */
  undo(): boolean {
    if (this.history.length === 0) return false;

    const previousState = this.history.pop()!;
    this.selected = new Set(previousState.selected);
    this.anchorId = previousState.anchorId;

    this.emit('change', Array.from(this.selected));
    this.persist();

    return true;
  }

  /**
   * Get selection history
   */
  getHistory(): SelectionState[] {
    return [...this.history];
  }

  /**
   * Clear history
   */
  clearHistory(): void {
    this.history = [];
  }

  // ========================================
  // EVENT SYSTEM
  // ========================================

  /**
   * Subscribe to selection events
   */
  on(event: SelectionEventType, listener: SelectionListener): () => void {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.add(listener);
    }

    // Return unsubscribe function
    return () => this.off(event, listener);
  }

  /**
   * Unsubscribe from selection events
   */
  off(event: SelectionEventType, listener: SelectionListener): void {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.delete(listener);
    }
  }

  /**
   * Emit selection event
   */
  protected emit(type: SelectionEventType, ids: string[]): void {
    const listeners = this.listeners.get(type);
    if (listeners) {
      const event: SelectionEvent = {
        type,
        ids,
        timestamp: Date.now()
      };
      listeners.forEach(listener => listener(event));
    }
  }

  // ========================================
  // PERSISTENCE
  // ========================================

  /**
   * Save state to storage
   */
  protected persist(): void {
    if (!this.options.persistent) return;

    try {
      const state = {
        selected: Array.from(this.selected),
        anchorId: this.anchorId,
        timestamp: Date.now()
      };
      localStorage.setItem(this.options.storageKey, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to persist selection state:', error);
    }
  }

  /**
   * Load state from storage
   */
  protected loadState(): void {
    if (!this.options.persistent) return;

    try {
      const stored = localStorage.getItem(this.options.storageKey);
      if (stored) {
        const state = JSON.parse(stored);
        this.selected = new Set(state.selected);
        this.anchorId = state.anchorId;
      }
    } catch (error) {
      console.error('Failed to load selection state:', error);
    }
  }

  /**
   * Clear persisted state
   */
  clearPersistedState(): void {
    if (!this.options.persistent) return;

    try {
      localStorage.removeItem(this.options.storageKey);
    } catch (error) {
      console.error('Failed to clear persisted state:', error);
    }
  }

  // ========================================
  // UTILITY METHODS
  // ========================================

  /**
   * Get selection mode
   */
  getMode(): 'single' | 'multiple' {
    return this.options.mode;
  }

  /**
   * Set selection mode
   */
  setMode(mode: 'single' | 'multiple'): void {
    this.options.mode = mode;

    // If switching to single mode, keep only first selected item
    if (mode === 'single' && this.selected.size > 1) {
      const firstId = Array.from(this.selected)[0];
      this.selected.clear();
      this.selected.add(firstId);
      this.anchorId = firstId;
      this.emit('change', [firstId]);
      this.persist();
    }
  }

  /**
   * Export state for debugging
   */
  exportState(): any {
    return {
      selected: Array.from(this.selected),
      anchorId: this.anchorId,
      mode: this.options.mode,
      historySize: this.history.length
    };
  }

  /**
   * Destroy manager and cleanup
   */
  destroy(): void {
    this.selected.clear();
    this.history = [];
    this.listeners.clear();
    this.anchorId = null;
  }
}
